import { useEffect, useState } from "react";
import { Plus, TrendingUp } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import {
  addCompensation,
  listCompensation,
  type CompensationEntry,
} from "@/api/employees";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";

interface Props {
  employeeId: string;
  canEdit: boolean;
}

const CHANGE_TYPES = [
  { value: "initial", label: "Initial" },
  { value: "merit", label: "Merit" },
  { value: "promotion", label: "Promotion" },
  { value: "cost_of_living", label: "Cost of living" },
  { value: "correction", label: "Correction" },
];

const PERIODS = [
  { value: "yearly", label: "Yearly" },
  { value: "monthly", label: "Monthly" },
  { value: "hourly", label: "Hourly" },
];

function formatMoney(amount: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function CompensationHistoryPanel({ employeeId, canEdit }: Props) {
  const { addToast } = useToast();
  const [entries, setEntries] = useState<CompensationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [period, setPeriod] = useState("yearly");
  const [changeType, setChangeType] = useState("merit");
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await listCompensation(employeeId);
      setEntries(data);
      setForbidden(false);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      if (ax.response?.status === 403) {
        setForbidden(true);
      } else {
        addToast("error", "Failed to load compensation history");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [employeeId]);

  const save = async () => {
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      addToast("error", "Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      await addCompensation(employeeId, {
        effective_date: effectiveDate,
        amount: num,
        currency,
        period,
        change_type: changeType,
        reason: reason || undefined,
      });
      addToast("success", "Compensation entry added");
      setOpen(false);
      setAmount("");
      setReason("");
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (forbidden) {
    return (
      <Card>
        <div className="text-center py-12 text-text-muted">
          <p className="text-sm">Compensation data requires HR access.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Compensation history</CardTitle>
        {canEdit && (
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            Add entry
          </Button>
        )}
      </CardHeader>
      {loading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-6">
          No compensation history recorded
        </p>
      ) : (
        <div className="relative">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border/60" />
          <ol className="space-y-4">
            {entries.map((e, i) => {
              const prev = entries[i + 1];
              const delta = prev ? e.amount - prev.amount : 0;
              return (
                <li key={e.id} className="relative pl-8">
                  <span className="absolute left-0 top-1 h-4 w-4 rounded-full bg-accent/20 border-2 border-accent" />
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-lg font-semibold text-text-primary">
                      {formatMoney(e.amount, e.currency)}
                    </span>
                    <span className="text-xs text-text-muted">per {e.period}</span>
                    {delta !== 0 && (
                      <Badge variant={delta > 0 ? "success" : "danger"}>
                        <TrendingUp
                          className={`h-3 w-3 ${delta < 0 ? "rotate-180" : ""}`}
                        />
                        {delta > 0 ? "+" : ""}
                        {formatMoney(delta, e.currency)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    {formatDate(e.effective_date)} · {e.change_type.replace("_", " ")}
                    {e.reason ? ` · ${e.reason}` : ""}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Add compensation entry">
        <div className="space-y-4">
          <Input
            label="Effective date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input
                label="Amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Currency
              </label>
              <Select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                options={[
                  { value: "USD", label: "USD" },
                  { value: "EUR", label: "EUR" },
                  { value: "GBP", label: "GBP" },
                ]}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Period
              </label>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                options={PERIODS}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Change type
              </label>
              <Select
                value={changeType}
                onChange={(e) => setChangeType(e.target.value)}
                options={CHANGE_TYPES}
              />
            </div>
          </div>
          <Input
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Annual merit review, promotion to..."
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={saving}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
